import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import AuthModal from '../modals/AuthModal';
import UnsubscribeModal from '../modals/UnsubscribeModal';
import brand from '../../config/brand';

const categories = [
  { slug: 'news', name: 'News' },
  { slug: 'personal-finance', name: 'Personal Finance' },
  { slug: 'debt-management', name: 'Debt Management' },
  { slug: 'small-business', name: 'Small Business' },
  { slug: 'investing', name: 'Investing' },
  { slug: 'markets', name: 'Markets' },
];

export default function Footer() {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [unsubscribeModalOpen, setUnsubscribeModalOpen] = useState(false);

  return (
    <footer className="footer bg-[#1E3A8A] border-t border-blue-800 pt-16 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div>
            <Link to="/" className="inline-block mb-4">
              <span className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                <span className="text-[#1D4ED8]">{brand.namePart1}</span>
                <span className="text-white">{brand.namePart2}</span>
              </span>
            </Link>
            <p className="text-blue-200 text-sm leading-relaxed mb-4">
              {brand.description}
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-blue-300 hover:text-white transition-colors" aria-label="Facebook">
                <Facebook className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <a href="#" className="text-blue-300 hover:text-white transition-colors" aria-label="Twitter">
                <Twitter className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <a href="#" className="text-blue-300 hover:text-white transition-colors" aria-label="Instagram">
                <Instagram className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <a href="#" className="text-blue-300 hover:text-white transition-colors" aria-label="LinkedIn">
                <Linkedin className="w-5 h-5" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Categories</h4>
            <ul className="space-y-2">
              {categories.map((cat) => (
                <li key={cat.slug}>
                  <Link
                    to={`/category/${cat.slug}`}
                    className="text-blue-200 hover:text-white text-sm transition-colors"
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/advertise" className="text-blue-200 hover:text-white text-sm transition-colors">
                  Advertise With Us
                </Link>
              </li>
              <li>
                <Link to="/archive" className="text-blue-200 hover:text-white text-sm transition-colors">
                  Archive
                </Link>
              </li>
              <li>
                <a href="#" className="text-blue-200 hover:text-white text-sm transition-colors">
                  Contact
                </a>
              </li>
              <li>
                <Link to="/privacy" className="text-blue-200 hover:text-white text-sm transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-blue-200 hover:text-white text-sm transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <button 
                  onClick={() => setUnsubscribeModalOpen(true)}
                  className="text-blue-200 hover:text-white text-sm transition-colors"
                  data-testid="unsubscribe-link"
                >
                  Unsubscribe
                </button>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Newsletter</h4>
            <p className="text-blue-200 text-sm mb-4">
              Get the latest financial news delivered to your inbox.
            </p>
            <Button
              onClick={() => setAuthModalOpen(true)}
              className="w-full bg-[#1D4ED8] text-white hover:bg-[#1E3A8A] font-semibold uppercase tracking-wide text-sm rounded-sm"
              data-testid="footer-subscribe-btn"
            >
              <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Subscribe
            </Button>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-blue-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-blue-300 text-sm">
              {new Date().getFullYear()} {brand.name}. All Rights Reserved.
            </p>
            <div className="flex items-center space-x-4">
              <a href={`mailto:${brand.contactEmail}`} className="flex items-center text-blue-200 hover:text-white text-sm transition-colors">
                <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {brand.contactEmail}
              </a>
            </div>
          </div>
        </div>
      </div>
      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} initialMode="register" />
      <UnsubscribeModal open={unsubscribeModalOpen} onClose={() => setUnsubscribeModalOpen(false)} />
    </footer>
  );
}
